/** The strip of figures at the top of Today and every engine page: four or
 *  five tiles, the first one dark when the server marks it the lead figure.
 *  Every word comes from the server; a tile with a `page` is a link. */
import { useNavigate } from 'react-router-dom';

import { Kpi } from '@/components/engine/api';
import { isPlainClick } from '@/components/header/sections';
import { surface } from '@/components/know-your-data/blocks';

const TONE: Record<string, string> = {
  bad: 'var(--sig-negative)',
  warn: 'var(--sig-warn-icon)'
};

export default function KpiStrip({
  kpis,
  lead = false
}: {
  kpis: Kpi[];
  /** Draw the first tile dark even when the server gave it no tone. */
  lead?: boolean;
}) {
  const navigate = useNavigate();
  if (!kpis.length) return null;
  const cols =
    kpis.length >= 5
      ? 'sm:grid-cols-3 lg:grid-cols-5'
      : kpis.length === 4
        ? 'sm:grid-cols-2 lg:grid-cols-4'
        : 'sm:grid-cols-3';
  return (
    <section aria-label="Key figures" className={`grid gap-3 ${cols}`}>
      {kpis.map((k, i) => {
        const dark = k.tone === 'ink' || (lead && i === 0 && !k.tone);
        const color = k.tone && TONE[k.tone] ? TONE[k.tone] : undefined;
        const body = (
          <>
            <span
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: dark ? '#C7D2FE' : 'var(--sig-muted)' }}
            >
              {k.label}
            </span>
            <span
              className="text-[26px] font-bold leading-tight tracking-tight tabular-nums"
              style={{ color: dark ? '#fff' : (color ?? 'var(--sig-ink)') }}
              dir="ltr"
            >
              {k.value}
            </span>
            {k.sub ? (
              <span
                className="text-xs leading-snug"
                style={{ color: dark ? '#C7D2FE' : 'var(--sig-muted)' }}
              >
                {k.sub}
              </span>
            ) : null}
          </>
        );
        const cls =
          'flex min-h-[6.5rem] min-w-0 flex-col gap-1 rounded-xl border p-4 transition-colors';
        const style = dark
          ? {
              background: 'var(--sig-blue-deep)',
              borderColor: 'var(--sig-blue-deep)',
              boxShadow: 'var(--sig-shadow-card)'
            }
          : { ...surface, boxShadow: 'var(--sig-shadow-card)' };
        return k.page ? (
          <a
            key={i}
            href={k.page}
            className={`${cls} hover:border-[var(--sig-blue)]`}
            style={style}
            onClick={(e) => {
              if (!isPlainClick(e)) return;
              e.preventDefault();
              navigate(k.page!);
            }}
          >
            {body}
          </a>
        ) : (
          <div key={i} className={cls} style={style}>
            {body}
          </div>
        );
      })}
    </section>
  );
}

/** The figures a page's own findings carry, for the pages without a strip of
 *  their own (Sales and Margin Signals): the first findings with a figure. */
export function kpisFromSections(
  sections: { blocks: { type: string; kpi?: string; kpi_label?: string }[] }[],
  max = 4
): Kpi[] {
  const out: Kpi[] = [];
  for (const s of sections) {
    for (const b of s.blocks) {
      if (b.type === 'finding' && b.kpi && b.kpi_label) {
        out.push({ label: b.kpi_label, value: b.kpi });
        if (out.length >= max) return out;
      }
    }
  }
  return out;
}
